// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useCallback, useEffect, useState } from 'react';
import {
  taxTablesApi,
  type ArfDrawdownBand,
  type CapitalAcquisitionsTax,
  type CapitalGainsTax,
  type CorporationTax,
  type ExitTax,
  type PensionEventTax,
  type PensionReliefBand,
  type TaxTables,
  type TaxYearSummary,
  type UscBand,
} from '../api/taxTablesApi';

/**
 * Platform-admin editor for the GLOBAL Irish tax tables that drive the
 * Relationship Cashflow tax engine. Structured form (rates shown as %, stored as
 * fractions). A year with no DB row is pre-filled from the code default; saving
 * materialises it. Edits are audited (TAX_TABLE, CRITICAL).
 * See docs/specs/cashflow-tax-modeling-prd.md.
 */
type NumKey = {
  [K in keyof TaxTables]-?: NonNullable<TaxTables[K]> extends number ? K : never;
}[keyof TaxTables];

/** The optional nested object groups, keyed by name, with their field types. */
type NestedGroups = {
  capitalGainsTax: CapitalGainsTax;
  exitTax: ExitTax;
  capitalAcquisitionsTax: CapitalAcquisitionsTax;
  corporationTax: CorporationTax;
  pensionEventTax: PensionEventTax;
};

/** Sensible empty defaults so a group can be initialised on first edit. */
const NESTED_DEFAULTS: { [G in keyof NestedGroups]: NestedGroups[G] } = {
  capitalGainsTax: {
    rate: 0,
    entrepreneurReliefRate: 0,
    entrepreneurReliefLifetimeLimit: 0,
    annualExemption: 0,
  },
  exitTax: { rate: 0, deemedDisposalYears: 0 },
  capitalAcquisitionsTax: {
    rate: 0,
    groupAThreshold: 0,
    groupBThreshold: 0,
    groupCThreshold: 0,
    smallGiftExemption: 0,
    businessReliefPct: 0,
    agriReliefPct: 0,
  },
  corporationTax: { tradingRate: 0, passiveRate: 0, closeCompanySurchargeRate: 0 },
  pensionEventTax: {
    lumpSumTaxFreeLimit: 0,
    lumpSumStandardRateBandUpper: 0,
    lumpSumStandardRate: 0,
    chargeableExcessTaxRate: 0,
    arfMinimumDrawdownBands: [],
    dbValuationFactor: 0,
  },
};

export default function TaxTablesConfigPage() {
  const [years, setYears] = useState<TaxYearSummary[]>([]);
  const [year, setYear] = useState<number | null>(null);
  const [tables, setTables] = useState<TaxTables | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const source = years.find((y) => y.taxYear === year)?.source;

  const loadYear = useCallback((y: number) => {
    setLoading(true);
    setError(null);
    setMessage(null);
    taxTablesApi
      .get(y)
      .then((t) => setTables(t))
      .catch((e) => setError(String(e)))
      .finally(() => setLoading(false));
  }, []);

  const refreshYears = useCallback(
    (selectAfter?: number) => {
      return taxTablesApi
        .listYears()
        .then((ys) => {
          setYears(ys);
          const pick = selectAfter ?? ys[0]?.taxYear ?? null;
          setYear(pick);
          if (pick != null) loadYear(pick);
          else setLoading(false);
        })
        .catch((e) => {
          setError(String(e));
          setLoading(false);
        });
    },
    [loadYear],
  );

  useEffect(() => {
    refreshYears();
  }, [refreshYears]);

  const setNum = (key: NumKey, raw: string, percent = false) => {
    setTables((t) => (t ? { ...t, [key]: toStored(raw, percent) } : t));
  };
  const setBand = (
    arr: 'uscBands' | 'uscReducedBands' | 'pensionReliefBands',
    idx: number,
    field: string,
    raw: string,
    percent = false,
  ) => {
    setTables((t) => {
      if (!t) return t;
      const next = [...(t[arr] as unknown as Array<Record<string, number>>)];
      next[idx] = { ...next[idx], [field]: toStored(raw, percent) };
      return { ...t, [arr]: next };
    });
  };
  const addBand = (arr: 'uscBands' | 'uscReducedBands' | 'pensionReliefBands') => {
    setTables((t) => {
      if (!t) return t;
      const blank = arr === 'pensionReliefBands' ? { maxAge: 0, pct: 0 } : { upTo: 0, rate: 0 };
      return { ...t, [arr]: [...(t[arr] as object[]), blank] };
    });
  };
  const removeBand = (arr: 'uscBands' | 'uscReducedBands' | 'pensionReliefBands', idx: number) => {
    setTables((t) =>
      t ? { ...t, [arr]: (t[arr] as object[]).filter((_, i) => i !== idx) } : t,
    );
  };

  // ─── nested object editing (capitalGainsTax, exitTax, …) ───
  const setNested = <G extends keyof NestedGroups>(
    group: G,
    field: keyof NestedGroups[G],
    raw: string,
    percent = false,
  ) => {
    setTables((t) => {
      if (!t) return t;
      const current = (t[group] as NestedGroups[G] | undefined) ?? NESTED_DEFAULTS[group];
      return { ...t, [group]: { ...current, [field as string]: toStored(raw, percent) } };
    });
  };

  // ─── nested band list editing (pensionEventTax.arfMinimumDrawdownBands) ───
  const arfBands = (t: PensionEventTax | undefined): ArfDrawdownBand[] =>
    t?.arfMinimumDrawdownBands ?? [];
  const setArfBand = (idx: number, field: keyof ArfDrawdownBand, raw: string, percent = false) => {
    setTables((t) => {
      if (!t) return t;
      const group = t.pensionEventTax ?? NESTED_DEFAULTS.pensionEventTax;
      const next = [...arfBands(group)];
      next[idx] = { ...next[idx], [field]: toStored(raw, percent) };
      return { ...t, pensionEventTax: { ...group, arfMinimumDrawdownBands: next } };
    });
  };
  const addArfBand = () => {
    setTables((t) => {
      if (!t) return t;
      const group = t.pensionEventTax ?? NESTED_DEFAULTS.pensionEventTax;
      return {
        ...t,
        pensionEventTax: {
          ...group,
          arfMinimumDrawdownBands: [...arfBands(group), { maxAge: 0, pct: 0 }],
        },
      };
    });
  };
  const removeArfBand = (idx: number) => {
    setTables((t) => {
      if (!t) return t;
      const group = t.pensionEventTax ?? NESTED_DEFAULTS.pensionEventTax;
      return {
        ...t,
        pensionEventTax: {
          ...group,
          arfMinimumDrawdownBands: arfBands(group).filter((_, i) => i !== idx),
        },
      };
    });
  };

  const save = () => {
    if (!tables || !year || saving) return;
    setSaving(true);
    setError(null);
    setMessage(null);
    taxTablesApi
      .save(year, tables)
      .then(() => refreshYears(year))
      .then(() => setMessage('Saved. This change affects every tenant and was audited.'))
      .catch((e) => setError(String(e)))
      .finally(() => setSaving(false));
  };

  const revert = () => {
    if (!year || saving) return;
    setSaving(true);
    setError(null);
    taxTablesApi
      .revertToDefault(year)
      .then(() => refreshYears())
      .then(() => setMessage('Reverted to the built-in default.'))
      .catch((e) => setError(String(e)))
      .finally(() => setSaving(false));
  };

  const addYear = () => {
    const latest = years[0]?.taxYear ?? new Date().getFullYear();
    const next = latest + 1;
    setYear(next);
    loadYear(next); // pre-fills from the code default
  };

  return (
    <div style={{ padding: 24, maxWidth: 960 }} data-testid="tax-tables-config-page">
      <h1>Tax Tables (Ireland)</h1>
      <p style={{ color: '#666' }}>
        Global figures driving the cashflow tax engine. Changes apply to every brokerage and are
        audited. A year with no saved row uses the built-in default.
      </p>

      <div style={{ display: 'flex', gap: 12, alignItems: 'center', margin: '12px 0' }}>
        <label htmlFor="tax-year-select">Tax year</label>
        <select
          id="tax-year-select"
          data-testid="tax-year-select"
          value={year ?? ''}
          onChange={(e) => {
            const y = Number(e.target.value);
            setYear(y);
            loadYear(y);
          }}
        >
          {years.map((y) => (
            <option key={y.taxYear} value={y.taxYear}>
              {y.taxYear} {y.source === 'DB' ? '(saved)' : '(default)'}
            </option>
          ))}
        </select>
        <button type="button" data-testid="tax-add-year" onClick={addYear}>
          + Add next year
        </button>
        {source && <span data-testid="tax-source" style={{ color: '#888' }}>source: {source}</span>}
      </div>

      {loading && <div data-testid="tax-loading">Loading…</div>}
      {error && <div data-testid="tax-error" style={{ color: '#C0392B' }}>{error}</div>}
      {message && <div data-testid="tax-message" style={{ color: '#1E8E4E' }}>{message}</div>}

      {tables && !loading && (
        <>
          <Section title="Provenance">
            <TextField label="Source URL" value={tables.sourceUri}
              onChange={(v) => setTables((t) => (t ? { ...t, sourceUri: v } : t))} testid="tax-sourceUri" />
            <TextField label="Effective date (yyyy-MM-dd)" value={tables.effectiveDate}
              onChange={(v) => setTables((t) => (t ? { ...t, effectiveDate: v } : t))} testid="tax-effectiveDate" />
          </Section>

          <Section title="Income tax">
            <Num label="Standard rate" percent k="standardRate" t={tables} on={setNum} />
            <Num label="Higher rate" percent k="higherRate" t={tables} on={setNum} />
            <Num label="Single band" k="standardRateBandSingle" t={tables} on={setNum} />
            <Num label="Married band (one income)" k="standardRateBandMarried" t={tables} on={setNum} />
            <Num label="Second-earner band cap" k="marriedSecondEarnerBandMax" t={tables} on={setNum} />
            <Num label="Single-parent band" k="standardRateBandSingleParent" t={tables} on={setNum} />
          </Section>

          <Section title="Tax credits">
            <Num label="Personal (single)" k="personalCreditSingle" t={tables} on={setNum} />
            <Num label="Personal (married)" k="personalCreditMarried" t={tables} on={setNum} />
            <Num label="Employee (PAYE)" k="employeeCredit" t={tables} on={setNum} />
            <Num label="Age credit (single)" k="ageCreditSingle" t={tables} on={setNum} />
            <Num label="Age credit (married)" k="ageCreditMarried" t={tables} on={setNum} />
            <Num label="Age credit qualifying age" k="ageCreditQualifyingAge" t={tables} on={setNum} />
          </Section>

          <Section title="USC">
            <Num label="Exemption threshold" k="uscExemptionThreshold" t={tables} on={setNum} />
            <Num label="Reduced-rate age" k="uscReducedAge" t={tables} on={setNum} />
            <Num label="Reduced-rate income limit" k="uscReducedIncomeLimit" t={tables} on={setNum} />
            <BandList title="USC bands" arr="uscBands" bands={tables.uscBands}
              fields={[{ k: 'upTo', label: 'Up to' }, { k: 'rate', label: 'Rate', percent: true }]}
              on={setBand} add={addBand} remove={removeBand} />
            <BandList title="USC reduced bands (70+/medical card)" arr="uscReducedBands" bands={tables.uscReducedBands}
              fields={[{ k: 'upTo', label: 'Up to' }, { k: 'rate', label: 'Rate', percent: true }]}
              on={setBand} add={addBand} remove={removeBand} />
          </Section>

          <Section title="PRSI & DIRT">
            <Num label="PRSI rate" percent k="prsiRate" t={tables} on={setNum} />
            <Num label="PRSI exempt age" k="prsiExemptAge" t={tables} on={setNum} />
            <Num label="DIRT rate" percent k="dirtRate" t={tables} on={setNum} />
          </Section>

          <Section title="Pension contribution relief">
            <Num label="Earnings cap" k="pensionEarningsCap" t={tables} on={setNum} />
            <BandList title="Age relief bands" arr="pensionReliefBands" bands={tables.pensionReliefBands}
              fields={[{ k: 'maxAge', label: 'Max age' }, { k: 'pct', label: '% of earnings', percent: true }]}
              on={setBand} add={addBand} remove={removeBand} />
          </Section>

          <Section title="Additional credits">
            <Num label="Earned income credit" k="earnedIncomeCredit" t={tables} on={setNum} />
            <Num label="Home carer credit" k="homeCarerCredit" t={tables} on={setNum} />
            <Num label="Single person child carer credit" k="singlePersonChildCarerCredit" t={tables} on={setNum} />
            <Num label="Incapacitated child credit" k="incapacitatedChildCredit" t={tables} on={setNum} />
            <Num label="Dependent relative credit" k="dependentRelativeCredit" t={tables} on={setNum} />
          </Section>

          <Section title="Self-employed PRSI & USC">
            <Num label="Self-employed PRSI rate" percent k="selfEmployedPrsiRate" t={tables} on={setNum} />
            <Num label="USC self-employed surcharge threshold" k="uscSelfEmployedSurchargeThreshold" t={tables} on={setNum} />
            <Num label="USC self-employed surcharge rate" percent k="uscSelfEmployedSurchargeRate" t={tables} on={setNum} />
          </Section>

          <Section title="Capital Gains Tax">
            <NestedNum label="Rate" percent group="capitalGainsTax" field="rate" t={tables} on={setNested} />
            <NestedNum label="Entrepreneur relief rate" percent group="capitalGainsTax" field="entrepreneurReliefRate" t={tables} on={setNested} />
            <NestedNum label="Entrepreneur relief lifetime limit" group="capitalGainsTax" field="entrepreneurReliefLifetimeLimit" t={tables} on={setNested} />
            <NestedNum label="Annual exemption" group="capitalGainsTax" field="annualExemption" t={tables} on={setNested} />
          </Section>

          <Section title="Exit tax & DWT">
            <NestedNum label="Exit tax rate" percent group="exitTax" field="rate" t={tables} on={setNested} />
            <NestedNum label="Deemed disposal years" group="exitTax" field="deemedDisposalYears" t={tables} on={setNested} />
            <Num label="Dividend withholding tax rate" percent k="dividendWithholdingTaxRate" t={tables} on={setNum} />
          </Section>

          <Section title="Capital Acquisitions Tax">
            <NestedNum label="Rate" percent group="capitalAcquisitionsTax" field="rate" t={tables} on={setNested} />
            <NestedNum label="Group A threshold" group="capitalAcquisitionsTax" field="groupAThreshold" t={tables} on={setNested} />
            <NestedNum label="Group B threshold" group="capitalAcquisitionsTax" field="groupBThreshold" t={tables} on={setNested} />
            <NestedNum label="Group C threshold" group="capitalAcquisitionsTax" field="groupCThreshold" t={tables} on={setNested} />
            <NestedNum label="Small gift exemption" group="capitalAcquisitionsTax" field="smallGiftExemption" t={tables} on={setNested} />
            <NestedNum label="Business relief %" percent group="capitalAcquisitionsTax" field="businessReliefPct" t={tables} on={setNested} />
            <NestedNum label="Agri relief %" percent group="capitalAcquisitionsTax" field="agriReliefPct" t={tables} on={setNested} />
          </Section>

          <Section title="Corporation tax">
            <NestedNum label="Trading rate" percent group="corporationTax" field="tradingRate" t={tables} on={setNested} />
            <NestedNum label="Passive rate" percent group="corporationTax" field="passiveRate" t={tables} on={setNested} />
            <NestedNum label="Close-company surcharge rate" percent group="corporationTax" field="closeCompanySurchargeRate" t={tables} on={setNested} />
          </Section>

          <Section title="Pension event tax">
            <NestedNum label="Lump-sum tax-free limit" group="pensionEventTax" field="lumpSumTaxFreeLimit" t={tables} on={setNested} />
            <NestedNum label="Lump-sum standard-rate band upper" group="pensionEventTax" field="lumpSumStandardRateBandUpper" t={tables} on={setNested} />
            <NestedNum label="Lump-sum standard rate" percent group="pensionEventTax" field="lumpSumStandardRate" t={tables} on={setNested} />
            <NestedNum label="Chargeable excess tax rate" percent group="pensionEventTax" field="chargeableExcessTaxRate" t={tables} on={setNested} />
            <NestedNum label="DB valuation factor" group="pensionEventTax" field="dbValuationFactor" t={tables} on={setNested} />
            <NestedBandList title="ARF minimum drawdown bands" bands={arfBands(tables.pensionEventTax)}
              on={setArfBand} add={addArfBand} remove={removeArfBand} />
          </Section>

          <div style={{ display: 'flex', gap: 12, marginTop: 16 }}>
            <button type="button" data-testid="tax-save" onClick={save} disabled={saving}>
              Save (affects all tenants)
            </button>
            {source === 'DB' && (
              <button type="button" data-testid="tax-revert" onClick={revert} disabled={saving}>
                Revert to default
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
}

// ───────────────────────── small field components ─────────────────────────

function toStored(raw: string, percent: boolean): number {
  const n = Number(raw);
  if (Number.isNaN(n)) return 0;
  return percent ? round(n / 100) : n;
}
function toDisplay(value: number, percent: boolean): string {
  return percent ? String(round6(value * 100)) : String(value);
}
function round(n: number): number {
  return Math.round(n * 1e6) / 1e6;
}
function round6(n: number): number {
  return Math.round(n * 1e6) / 1e6;
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <fieldset style={{ margin: '12px 0', padding: 12, border: '1px solid #ddd', borderRadius: 8 }}>
      <legend style={{ fontWeight: 600 }}>{title}</legend>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12 }}>{children}</div>
    </fieldset>
  );
}

function TextField({
  label, value, onChange, testid,
}: { label: string; value: string; onChange: (v: string) => void; testid: string }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', fontSize: 13 }}>
      <span>{label}</span>
      <input data-testid={testid} value={value ?? ''} onChange={(e) => onChange(e.target.value)} />
    </label>
  );
}

function Num({
  label, k, t, on, percent = false,
}: {
  label: string;
  k: NumKey;
  t: TaxTables;
  on: (k: NumKey, raw: string, percent?: boolean) => void;
  percent?: boolean;
}) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', fontSize: 13 }}>
      <span>{label} {percent && <span style={{ color: '#888' }}>(%)</span>}</span>
      <input type="number" data-testid={`tax-${k}`} value={toDisplay((t[k] as number | undefined) ?? 0, percent)}
        onChange={(e) => on(k, e.target.value, percent)} />
    </label>
  );
}

function NestedNum<G extends keyof NestedGroups>({
  label, group, field, t, on, percent = false,
}: {
  label: string;
  group: G;
  field: keyof NestedGroups[G];
  t: TaxTables;
  on: <GG extends keyof NestedGroups>(group: GG, field: keyof NestedGroups[GG], raw: string, percent?: boolean) => void;
  percent?: boolean;
}) {
  const obj = t[group] as NestedGroups[G] | undefined;
  const value = (obj ? (obj[field] as unknown as number) : undefined) ?? 0;
  return (
    <label style={{ display: 'flex', flexDirection: 'column', fontSize: 13 }}>
      <span>{label} {percent && <span style={{ color: '#888' }}>(%)</span>}</span>
      <input type="number" data-testid={`tax-${group}-${String(field)}`} value={toDisplay(value, percent)}
        onChange={(e) => on(group, field, e.target.value, percent)} />
    </label>
  );
}

function NestedBandList({
  title, bands, on, add, remove,
}: {
  title: string;
  bands: ArfDrawdownBand[];
  on: (idx: number, field: keyof ArfDrawdownBand, raw: string, percent?: boolean) => void;
  add: () => void;
  remove: (idx: number) => void;
}) {
  const arr = 'arfMinimumDrawdownBands';
  const fields: Array<{ k: keyof ArfDrawdownBand; label: string; percent?: boolean }> = [
    { k: 'maxAge', label: 'Max age' },
    { k: 'pct', label: 'Drawdown', percent: true },
  ];
  return (
    <div style={{ gridColumn: '1 / -1' }} data-testid={`tax-bandlist-${arr}`}>
      <div style={{ fontWeight: 600, fontSize: 13, margin: '4px 0' }}>{title}</div>
      {bands.map((b, i) => (
        <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 6 }}>
          {fields.map((f) => (
            <label key={f.k} style={{ fontSize: 12 }}>
              {f.label}{f.percent ? ' (%)' : ''}{' '}
              <input
                type="number"
                data-testid={`tax-${arr}-${i}-${f.k}`}
                value={toDisplay(b[f.k], !!f.percent)}
                onChange={(e) => on(i, f.k, e.target.value, f.percent)}
              />
            </label>
          ))}
          <button type="button" data-testid={`tax-${arr}-${i}-remove`} onClick={() => remove(i)}>
            −
          </button>
        </div>
      ))}
      <button type="button" data-testid={`tax-${arr}-add`} onClick={add}>
        + Add row
      </button>
    </div>
  );
}

function BandList({
  title, arr, bands, fields, on, add, remove,
}: {
  title: string;
  arr: 'uscBands' | 'uscReducedBands' | 'pensionReliefBands';
  bands: Array<UscBand | PensionReliefBand>;
  fields: Array<{ k: string; label: string; percent?: boolean }>;
  on: (arr: 'uscBands' | 'uscReducedBands' | 'pensionReliefBands', idx: number, field: string, raw: string, percent?: boolean) => void;
  add: (arr: 'uscBands' | 'uscReducedBands' | 'pensionReliefBands') => void;
  remove: (arr: 'uscBands' | 'uscReducedBands' | 'pensionReliefBands', idx: number) => void;
}) {
  return (
    <div style={{ gridColumn: '1 / -1' }} data-testid={`tax-bandlist-${arr}`}>
      <div style={{ fontWeight: 600, fontSize: 13, margin: '4px 0' }}>{title}</div>
      {bands.map((b, i) => (
        <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 6 }}>
          {fields.map((f) => (
            <label key={f.k} style={{ fontSize: 12 }}>
              {f.label}{f.percent ? ' (%)' : ''}{' '}
              <input
                type="number"
                data-testid={`tax-${arr}-${i}-${f.k}`}
                value={toDisplay((b as unknown as Record<string, number>)[f.k], !!f.percent)}
                onChange={(e) => on(arr, i, f.k, e.target.value, f.percent)}
              />
            </label>
          ))}
          <button type="button" data-testid={`tax-${arr}-${i}-remove`} onClick={() => remove(arr, i)}>
            −
          </button>
        </div>
      ))}
      <button type="button" data-testid={`tax-${arr}-add`} onClick={() => add(arr)}>
        + Add row
      </button>
    </div>
  );
}
